/**
 * FullscreenManager - менеджер для определения и управления полноэкранным режимом
 * 
 * Полный набор способов определить "полноэкранность" страницы.
 * Включает:
 * - Fullscreen API + вендорные префиксы
 * - Специальные флаги старых браузеров (window.fullScreen, document.webkitIsFullScreen, document.mozFullScreen)
 * - PWA display-mode (fullscreen/standalone/minimal-ui)
 * - Эвристики размеров окна/экрана, позиционирования окна (F11 и пр.)
 * - Слушатели событий (fullscreenchange + префиксы, resize, orientationchange, visibilitychange, webkitbeginfullscreen/webkitendfullscreen)
 *
 * ВАЖНО: "Все способы" != 100% точность. F11/мульти‑мониторы/панели задач/мобильные UI могут давать ложные срабатывания.
 * Используйте итоговый признак likelyFullscreen осмотрительно, при необходимости включайте/отключайте отдельные источники через options.
 */
export class FullscreenManager {
  static FS_CHANGE_EVENTS = [
    "fullscreenchange",
    "webkitfullscreenchange",
    "mozfullscreenchange",
    "MSFullscreenChange",
  ];

  // iOS Safari для <video> кидает эти события на самом элементе (не всегда на документе).
  static WEBKIT_VIDEO_FS_EVENTS = ["webkitbeginfullscreen", "webkitendfullscreen"];

  /**
   * Проверяет равенство двух чисел с погрешностью
   * @param {number} a 
   * @param {number} b 
   * @param {number} eps - погрешность
   * @returns {boolean}
   */
  static nearEqual(a, b, eps = 2) {
    return Math.abs((a || 0) - (b || 0)) <= eps;
  }

  /**
   * Безопасное получение числового значения
   * @param {any} v 
   * @param {number} fallback 
   * @returns {number}
   */
  static safeNum(v, fallback = 0) {
    return typeof v === "number" && isFinite(v) ? v : fallback;
  }

  /**
   * Получает размеры окна и экрана
   * @returns {object}
   */
  static getSizes() {
    const dpr = FullscreenManager.safeNum(window.devicePixelRatio, 1);

    const innerWidth = FullscreenManager.safeNum(window.innerWidth);
    const innerHeight = FullscreenManager.safeNum(window.innerHeight);

    const outerWidth = FullscreenManager.safeNum(window.outerWidth, innerWidth);
    const outerHeight = FullscreenManager.safeNum(window.outerHeight, innerHeight);

    const screenWidth = FullscreenManager.safeNum(screen && screen.width);
    const screenHeight = FullscreenManager.safeNum(screen && screen.height);

    const availWidth = FullscreenManager.safeNum(screen && screen.availWidth, screenWidth);
    const availHeight = FullscreenManager.safeNum(screen && screen.availHeight, screenHeight);

    const vv = window.visualViewport;
    const vvWidth = FullscreenManager.safeNum(vv && vv.width, innerWidth);
    const vvHeight = FullscreenManager.safeNum(vv && vv.height, innerHeight);

    const screenX = FullscreenManager.safeNum(window.screenX ?? window.screenLeft, 0);
    const screenY = FullscreenManager.safeNum(window.screenY ?? window.screenTop, 0);

    return {
      dpr,
      innerWidth,
      innerHeight,
      outerWidth,
      outerHeight,
      screenWidth,
      screenHeight,
      availWidth,
      availHeight,
      vvWidth,
      vvHeight,
      screenX,
      screenY,
    };
  }

  /**
   * Получает состояние Fullscreen API
   * @param {Document} doc 
   * @returns {object}
   */
  static getFullscreenAPIState(doc = document) {
    const element =
      doc.fullscreenElement ||
      doc.webkitFullscreenElement ||
      doc.mozFullScreenElement ||
      doc.msFullscreenElement ||
      // крайне старые webkit:
      doc.webkitCurrentFullScreenElement ||
      null;

    const enabled =
      !!(
        doc.fullscreenEnabled ||
        doc.webkitFullscreenEnabled ||
        doc.mozFullScreenEnabled ||
        doc.msFullscreenEnabled
      );

    // Старые флаги состояния
    const webkitIsFullScreen = !!doc.webkitIsFullScreen;
    const mozFullScreen = !!doc.mozFullScreen;

    return { element, enabled, webkitIsFullScreen, mozFullScreen };
  }

  /**
   * Получает флаги окна
   * @param {Window} win 
   * @returns {object}
   */
  static getWindowFlags(win = window) {
    // В ряде браузеров (особенно старые Firefox) при F11 менялся window.fullScreen
    const windowFullScreen =
      typeof win.fullScreen === "boolean" ? win.fullScreen : undefined;

    return { windowFullScreen };
  }

  /**
   * Получает режим отображения (PWA)
   * @returns {object}
   */
  static getDisplayMode() {
    // PWA режимы
    const mm = (q) => {
      try {
        return window.matchMedia && window.matchMedia(q).matches;
      } catch {
        return false;
      }
    };

    const fullscreen = mm("(display-mode: fullscreen)");
    const standalone = mm("(display-mode: standalone)");
    const minimalUi = mm("(display-mode: minimal-ui)");
    const browser = mm("(display-mode: browser)");

    // iOS Safari, если иконка добавлена на домашний экран
    const pwaStandaloneLegacy = !!(window.navigator && window.navigator.standalone);

    return { fullscreen, standalone, minimalUi, browser, pwaStandaloneLegacy };
  }

  /**
   * Получает эвристические данные
   * @param {number} eps - погрешность
   * @returns {object}
   */
  static getHeuristics(eps = 2) {
    const s = FullscreenManager.getSizes();

    // Основные сравнения размеров
    const viewportEqualsScreen =
      FullscreenManager.nearEqual(s.innerWidth, s.screenWidth, eps) &&
      FullscreenManager.nearEqual(s.innerHeight, s.screenHeight, eps);

    const outerEqualsScreen =
      FullscreenManager.nearEqual(s.outerWidth, s.screenWidth, eps) &&
      FullscreenManager.nearEqual(s.outerHeight, s.screenHeight, eps);

    const availEqualsScreen =
      FullscreenManager.nearEqual(s.innerWidth, s.availWidth, eps) &&
      FullscreenManager.nearEqual(s.innerHeight, s.availHeight, eps);

    const visualViewportEqualsScreen =
      FullscreenManager.nearEqual(s.vvWidth, s.screenWidth, eps) &&
      FullscreenManager.nearEqual(s.vvHeight, s.screenHeight, eps);

    // Позиция окна в (0,0) часто сопутствует F11
    const atTopLeft =
      FullscreenManager.nearEqual(s.screenX, 0, Math.max(2, Math.ceil(s.dpr))) &&
      FullscreenManager.nearEqual(s.screenY, 0, Math.max(2, Math.ceil(s.dpr)));

    // Общая "близость" по любому из критериев
    const nearByAny =
      viewportEqualsScreen ||
      outerEqualsScreen ||
      availEqualsScreen ||
      visualViewportEqualsScreen;

    return {
      eps,
      viewportEqualsScreen,
      outerEqualsScreen,
      availEqualsScreen,
      visualViewportEqualsScreen,
      atTopLeft,
      nearByAny,
      sizes: s,
    };
  }

  /**
   * Получает полное состояние полноэкранного режима
   * @param {object} options - параметры
   * @returns {object}
   */
  static getFullscreenState(options = {}) {
    const {
      eps = 2,
      countStandaloneAsFullscreen = false, // считать display-mode: standalone как fullscreen
      requireTopLeftForHeuristic = false, // требовать (0,0) для эвристики
      minSignalsForHeuristic = 1, // сколько эвристик из {viewport, outer, avail, visualViewport} нужно для true
    } = options;

    const api = FullscreenManager.getFullscreenAPIState();
    const win = FullscreenManager.getWindowFlags();
    const dm = FullscreenManager.getDisplayMode();
    const hs = FullscreenManager.getHeuristics(eps);

    // 1) Явный Fullscreen API
    if (api.element || api.webkitIsFullScreen || api.mozFullScreen) {
      return {
        likelyFullscreen: true,
        source: "api",
        api,
        win,
        displayMode: dm,
        heuristics: hs,
      };
    }

    // 2) Старый флаг окна
    if (win.windowFullScreen === true) {
      return {
        likelyFullscreen: true,
        source: "window.fullScreen",
        api,
        win,
        displayMode: dm,
        heuristics: hs,
      };
    }

    // 3) PWA/Display-mode (часто действительно полноэкран или "как приложение")
    if (dm.fullscreen) {
      return {
        likelyFullscreen: true,
        source: "display-mode:fullscreen",
        api,
        win,
        displayMode: dm,
        heuristics: hs,
      };
    }

    if (countStandaloneAsFullscreen && (dm.standalone || dm.pwaStandaloneLegacy)) {
      return {
        likelyFullscreen: true,
        source: "display-mode:standalone",
        api,
        win,
        displayMode: dm,
        heuristics: hs,
      };
    }

    // 4) Эвристики (F11 и проч.)
    const signals =
      (hs.viewportEqualsScreen ? 1 : 0) +
      (hs.outerEqualsScreen ? 1 : 0) +
      (hs.availEqualsScreen ? 1 : 0) +
      (hs.visualViewportEqualsScreen ? 1 : 0);

    const heuristicsOk =
      signals >= Math.max(1, minSignalsForHeuristic) &&
      (!requireTopLeftForHeuristic || hs.atTopLeft);

    if (heuristicsOk) {
      return {
        likelyFullscreen: true,
        source: "heuristic",
        api,
        win,
        displayMode: dm,
        heuristics: hs,
      };
    }

    // 5) По умолчанию
    return {
      likelyFullscreen: false,
      source: "none",
      api,
      win,
      displayMode: dm,
      heuristics: hs,
    };
  }

  /**
   * Проверяет, находится ли страница в полноэкранном режиме
   * @param {object} options - параметры
   * @returns {boolean}
   */
  static isFullscreen(options = {}) {
    return FullscreenManager.getFullscreenState(options).likelyFullscreen;
  }

  /**
   * Входит в полноэкранный режим
   * @param {Element} el - элемент для полноэкранного режима
   * @returns {Promise}
   */
  static async enterFullscreen(el = document.documentElement) {
    const req =
      el.requestFullscreen ||
      el.webkitRequestFullscreen ||
      el.mozRequestFullScreen ||
      el.msRequestFullscreen;

    if (req) {
      const p = req.call(el);
      if (p && typeof p.then === "function") return p;
      return Promise.resolve();
    }
    return Promise.reject(new Error("Fullscreen API is not available"));
  }

  /**
   * Выходит из полноэкранного режима
   * @returns {Promise}
   */
  static async exitFullscreen() {
    const doc = document;
    const exit =
      doc.exitFullscreen ||
      doc.webkitExitFullscreen ||
      doc.mozCancelFullScreen ||
      doc.msExitFullscreen;

    if (exit) {
      const p = exit.call(doc);
      if (p && typeof p.then === "function") return p;
      return Promise.resolve();
    }
    return Promise.reject(new Error("Fullscreen API is not available"));
  }

  /**
   * Подписывается на изменения полноэкранного режима
   * @param {Function} callback - колбэк функция
   * @param {object} options - параметры
   * @returns {Function} - функция отписки
   */
  static watchFullscreen(callback, options = {}) {
    if (typeof callback !== "function") {
      throw new Error("callback must be a function");
    }

    const {
      throttleMs = 50,
      listenResize = true,
      listenOrientation = true,
      listenVisibility = true,
      listenWebkitVideo = true,
    } = options;

    let rafId = 0;
    let lastCall = 0;

    const invoke = () => {
      callback(FullscreenManager.getFullscreenState());
    };

    const throttled = () => {
      const now = Date.now();
      if (now - lastCall >= throttleMs) {
        lastCall = now;
        invoke();
      } else {
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(invoke);
      }
    };

    const onFsChange = throttled;
    FullscreenManager.FS_CHANGE_EVENTS.forEach((e) =>
      document.addEventListener(e, onFsChange, { passive: true })
    );

    const onResize = throttled;
    if (listenResize) window.addEventListener("resize", onResize, { passive: true });

    const onOrientation = throttled;
    if (listenOrientation) window.addEventListener("orientationchange", onOrientation, { passive: true });

    const onVisibility = throttled;
    if (listenVisibility) document.addEventListener("visibilitychange", onVisibility, { passive: true });

    // Перехват webkit begin/end fullscreen (в основном для iOS <video>)
    const webkitHandlers = [];
    if (listenWebkitVideo) {
      FullscreenManager.WEBKIT_VIDEO_FS_EVENTS.forEach((evt) => {
        const handler = throttled;
        // Слушаем на document в режиме перехвата — события всплывают с <video>
        document.addEventListener(evt, handler, { passive: true, capture: true });
        webkitHandlers.push({ evt, handler });
      });
    }

    // первый вызов
    invoke();

    return () => {
      FullscreenManager.FS_CHANGE_EVENTS.forEach((e) =>
        document.removeEventListener(e, onFsChange)
      );
      if (listenResize) window.removeEventListener("resize", onResize);
      if (listenOrientation) window.removeEventListener("orientationchange", onOrientation);
      if (listenVisibility) document.removeEventListener("visibilitychange", onVisibility);
      webkitHandlers.forEach(({ evt, handler }) =>
        document.removeEventListener(evt, handler, { capture: true })
      );
      cancelAnimationFrame(rafId);
    };
  }
}
